import { useUserSession } from "@/hooks/useUserSession";
import { Box, Button, Image, Text, useToast } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { authIa } from "@/lib/firebase";
import { updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { signOut } from "firebase/auth";
import { useTranslation } from "react-i18next";

const EmailVerification = () => {
  const [delay, setDelay] = useState(0);
  const [launchEffect, setLaunchEffect] = useState(false);

  const { user } = useUserSession();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();

  const welcomeEmail = httpsCallable(functions, "sendWelcomeEmail");
  const confirmationEmail = httpsCallable(functions, "sendConfirmationEmail");

  //   Listener to emailVerified
  useEffect(() => {
    if (!user || user.emailVerified) return;

    const savedDelay = localStorage.getItem("emailVerificationDelay");
    if (!savedDelay || new Date().toISOString() < savedDelay) {
      setDelay(60);
    }

    if (
      new Date(user.createdAt.seconds * 1000).toISOString() >
      // TODO - Start date checking, need to move it in .env
      new Date("2025-03-24").toISOString()
    ) {
      const interval = setInterval(async () => {
        await authIa.currentUser?.reload();
        const updatedUser = authIa.currentUser;

        if (updatedUser?.emailVerified) {
          updateDoc(user.ref, { emailVerified: true });

          toast({
            title: t("auth.emailVerification.toast.title"),
            description: t("auth.emailVerification.toast.description"),
            status: "success",
            duration: 9000,
            isClosable: true,
            position: "top",
          });

          await welcomeEmail({
            firstname: user?.firstName,
            lastname: user?.lastName,
            email: user?.email,
          });

          clearInterval(interval);
        }
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [user]);

  //   Call CF when landing on page & redirection
  useEffect(() => {
    (async () => {
      const logFromExternProvider = authIa?.currentUser?.providerData?.some(
        (provider) =>
          provider.providerId === "google.com" ||
          provider.providerId === "apple.com"
      );

      if (!user || user.emailVerified || logFromExternProvider) {
        return navigate("/", { replace: true });
      }
      if (launchEffect) return;
      setLaunchEffect(true);

      const savedDelay = localStorage.getItem("emailVerificationDelay");
      const now = new Date().toISOString();

      if (!savedDelay || now > savedDelay) {
        localStorage.setItem(
          "emailVerificationDelay",
          new Date(Date.now() + 60000).toISOString()
        );
        await confirmationEmail({
          firstname: user?.firstName,
          lastname: user?.lastName,
          email: user?.email,
        });
      }
    })();
  }, [user, launchEffect, confirmationEmail, navigate]);

  //  Timer
  useEffect(() => {
    if (delay > 0) {
      const interval = setInterval(() => {
        setDelay((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [delay]);

  const logout = () => {
    signOut(authIa);
  };

  const handleResendEmail = async () => {
    if (delay > 0) return;

    const newDelay = new Date(Date.now() + 60000).toISOString();
    localStorage.setItem("emailVerificationDelay", newDelay);
    setDelay(60);

    await confirmationEmail({
      firstname: user?.firstName,
      lastname: user?.lastName,
      email: user?.email,
    });
    setLaunchEffect(true);
  };

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 20,
        flex: 1,
        width: "100%",
      }}
    >
      <Box
        maxW={window.innerWidth < 700 ? "95%" : 650}
        bgImage={"/backgrounds/auth.svg"}
        style={{
          boxShadow: "0px 0px 5px 0px rgba(0, 0, 0, 0.5)",
          borderRadius: 40,
          padding: 0,
          backgroundPosition: "top right",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0,0,0,0.4)",
            borderRadius: 40,
            padding: 20,
          }}
        >
          <Image src="/images/logo-full-white.svg" />
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 10,
              borderBottom: "1px solid #dce7f2",
              paddingBottom: 20,
              marginBottom: 20,
              color: "#fff",
            }}
          >
            <Text
              style={{
                borderBottom: "3px solid #00BCB0",
                padding: "5px 10px",
                fontSize: 22,
                fontWeight: 900,
              }}
            >
              {t("auth.emailVerification.description.0")}
            </Text>
            <Text>
              {t("auth.emailVerification.description.1")}
              <Text fontWeight={900} display={"inline"}>
                {t("auth.emailVerification.description.2")}
              </Text>
              {t("auth.emailVerification.description.3")}
            </Text>
            <Text display={"inline"} style={{ borderBottom: "1px solid grey" }}>
              {user?.email}
            </Text>
            <Text style={{ width: "100%" }}>
              {t("auth.emailVerification.description.4")}
            </Text>
          </Box>
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Text fontSize={14} textAlign={"center"} color={"#fff"}>
              {t("auth.emailVerification.forgot.text")}
            </Text>
            <Button
              disabled={delay > 0}
              onClick={handleResendEmail}
              style={{
                backgroundColor: "#00BCB0",
                color: "#fff",
                padding: "10px 20px",
                borderRadius: 0,
                width: 175,
              }}
            >
              {delay > 0
                ? `${t("auth.emailVerification.forgot.button.wait")} ${delay}s`
                : t("auth.emailVerification.forgot.button.send")}
            </Button>
          </Box>
        </Box>
      </Box>
      <Button
        aria-label={"Logout"}
        bgColor="white"
        gap={2}
        filter={"drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.25));"}
        onClick={logout}
      >
        <Image src="/icons/exit.svg" />
        <Text>{t("auth.emailVerification.logout")}</Text>
      </Button>
    </Box>
  );
};

export default EmailVerification;
